import AppBarChart from '@/components/AppBarChart';
import AppComposedChart from '@/components/AppComposedChart';
import AppTableControl from '@/components/AppTableControl';
import AppPageTitle from '@/components/AppPageTitle';
// import ExcelExporter from '@/components/common/ExcelExporter';
import React, { useMemo, useState } from 'react';
import { Bar } from 'recharts';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getRevenueReport } from '@/services/revenue.service';
import { isArray } from 'lodash';
import dayjs from 'dayjs';
import Details from './Details';
import useUniquePackages from '@/hooks/useUniquePackages';
import OverviewTable from './OverviewTable';
import ExcelExporter from '@/components/common/ExcelExporter';
import { Button, Tooltip } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';

const selectItems = [
  {
    value: null,
    label: 'All',
  },
  {
    value: 1,
    label: 'Sub Package',
  },
  {
    value: 2,
    label: 'Retail Package',
  },
];

const START_DATE = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
const END_DATE = dayjs().format('YYYY-MM-DD');

function Dashboard() {
  const [date, setDate] = useState([START_DATE, END_DATE]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const { data, refetch, isRefetching, isLoading } = usePaginatedQuery(
    () =>
      getRevenueReport({
        start_date: date[0],
        end_date: date[1],
        packageOption: selectedFilter,
      }),
    ['report-revenue', date, selectedFilter]
  );
  const [openDrawer, setOpenDrawer] = useState(false);

  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.mdPlus}px)`);

  const isBreak = useMediaQuery(`(max-width: 1400px)`);

  const uniquePackages = useUniquePackages(selectedFilter);

  const revenueQuantity = useMemo(() => {
    if (!uniquePackages || uniquePackages.length === 0) return { packageData: [], totals: null };

    const groupPackage = new Map();

    const totals = {
      key: 'TOTAL',
      package_code: 'TOTAL',
      quantity: 0,
      total_revenue: 0,
      total_register: 0,
      total_renew: 0,
      sms: 0,
      ussd: 0,
      web_wap: 0,
    };

    uniquePackages.forEach(({ key, name }) => {
      groupPackage.set(key, {
        quantity: 0,
        total_revenue: 0,
        total_register: 0,
        total_renew: 0,
        sms: 0,
        ussd: 0,
        web_wap: 0,
        name: name, // Store the display name
      });
    });

    if (data && isArray(data) && data.length > 0) {
      data.forEach((item) => {
        const itemPackage = item.package_code;

        if (groupPackage.has(itemPackage)) {
          const stats = groupPackage.get(itemPackage);
          const totalRevenue = item.total_revenue || 0;
          const totalRegister = item.total_reg_revenue || 0;
          const totalRenew = item.total_renew_revenue || 0;
          const totalQuantity = item.total_reg_renew || 0;

          stats.quantity += totalQuantity;
          stats.total_revenue += totalRevenue;
          stats.total_register += totalRegister;
          stats.total_renew += totalRenew;

          // Update totals
          totals.quantity += totalQuantity;
          totals.total_revenue += totalRevenue;
          totals.total_register += totalRegister;
          totals.total_renew += totalRenew;

          if (item.channel === 'SMS') {
            stats.sms += totalRevenue;
            totals.sms += totalRevenue;
          } else if (item.channel === 'USSD') {
            stats.ussd += totalRevenue;
            totals.ussd += totalRevenue;
          } else if (item.channel === 'WEB' || item.channel === 'WAP') {
            stats.web_wap += totalRevenue;
            totals.web_wap += totalRevenue;
          }
        }
      });
    }

    const packageData = Array.from(groupPackage.entries()).map(([packageCode, stats]) => {
      return {
        key: packageCode,
        package_code: stats.name, // Use the stored display name
        quantity: stats.quantity,
        sms: stats.sms,
        ussd: stats.ussd,
        web_wap: stats.web_wap,
        total_register: stats.total_register,
        total_renew: stats.total_renew,
        total_revenue: stats.total_revenue,
      };
    });

    return { packageData, totals };
  }, [data, uniquePackages]);

  const revenueChannel = useMemo(() => {
    const predefinedChannels = ['SMS', 'USSD', 'WEB/WAP', 'SYSTEM'];

    const channelRevenue = predefinedChannels.reduce((acc, channel) => {
      acc[channel] = 0;
      return acc;
    }, {});

    if (data && isArray(data) && data.length > 0) {
      data.forEach((item) => {
        const itemChannel = item.channel?.toUpperCase();

        const normalizedChannel = itemChannel === 'WEB' ? 'WEB/WAP' : itemChannel;

        if (predefinedChannels.includes(normalizedChannel)) {
          channelRevenue[normalizedChannel] += item.total_revenue || 0;
        }
      });
    }

    const result = predefinedChannels.map((channel) => ({
      channel,
      total_revenue: channelRevenue[channel],
    }));

    return result;
  }, [data]);

  const packageDataArray = useMemo(() => revenueQuantity?.packageData || [], [revenueQuantity]);
  const overviewTotals = revenueQuantity?.totals || null;

  const revenueQuantityWithTotals = useMemo(() => {
    if (!packageDataArray.length || !overviewTotals) return [];
    return [...packageDataArray, overviewTotals];
  }, [packageDataArray, overviewTotals]);

  return (
    <div className="flex flex-col items-start w-full">
      <AppPageTitle
        title="Revenue"
        extra={
          <Tooltip title="Refresh">
            <Button onClick={refetch} disabled={isRefetching || isLoading} icon={<SyncOutlined />}>
              Sync
            </Button>
          </Tooltip>
        }
      />
      {/* {isMobile && <OpenDrawerBtn onClick={() => setOpenDrawer(true)} />} */}
      <AppTableControl
        selectOptions={selectItems}
        selectValue={selectedFilter}
        setSelectValue={setSelectedFilter}
        date={date}
        setDate={setDate}
        dateLimit={{
          value: 30,
          unit: 'day',
        }}
        noStyle
        open={openDrawer}
        setOpen={setOpenDrawer}
      />

      <div className="flex items-center justify-between w-full mt-6">
        <h2 className="text-2xl font-medium ">Overview</h2>
        <ExcelExporter
          data={revenueQuantityWithTotals}
          fileName={`revenue_overview_${date[0]}_${date[1]}.xlsx`}
          columnMapping={{
            package_code: 'Package Code',
            quantity: 'Quantity',
            sms: 'SMS Revenue',
            ussd: 'USSD Revenue',
            web_wap: 'Web/WAP Revenue',
            total_register: 'Total Registration Revenue',
            total_renew: 'Total Renewal Revenue',
            total_revenue: 'Total Revenue',
          }}
        />
      </div>
      <div
        className={`w-full flex items-center ${isMobile ? 'flex-col' : 'flex-row h-[700px]'} mt-4 gap-2`}
      >
        <div className={`flex flex-col w-full ${isMobile ? 'h-[500px] mb-6' : 'h-full'} gap-2`}>
          <p className="font-semibold">Overview Quantity - Revenue Chart</p>
          <AppComposedChart data={packageDataArray} loading={isLoading || isRefetching} />
        </div>
        <div className={`flex flex-col w-full ${isMobile ? 'h-[500px] mb-4' : 'h-full'} gap-2`}>
          <p className="font-semibold">Revenue of channel chart</p>
          <AppBarChart
            data={revenueChannel}
            xAxisLabel="Channel"
            yAxisLabel="Revenue"
            xAxisDataKey="channel"
            loading={isLoading || isRefetching}
          >
            <Bar dataKey="total_revenue" fill="#6a9eeb" />
          </AppBarChart>
        </div>
      </div>

      <OverviewTable
        dataSource={packageDataArray}
        totals={overviewTotals}
        loading={isLoading || isRefetching}
      />

      <Details
        dataSource={data}
        date={date}
        uniquePackages={uniquePackages}
        loading={isLoading || isRefetching}
      />
    </div>
  );
}

export default Dashboard;
